import { Alert, Anchor, Button, Checkbox, Divider, Flex, Image, PasswordInput, Text, TextInput, Box, Loader } from "@mantine/core";
import { ActionFunctionArgs, json, LoaderFunctionArgs, redirect } from "@remix-run/node";
import { Form, useActionData, useLoaderData, useNavigate, useNavigation, useSearchParams } from "@remix-run/react";
import { authenticator, googleSsoEnabled } from "~/auth.server";
import { getSession, commitSession, destroySession } from "session.server";
import TrashFairiesLogo from "~/assets/svg/TrashFairiesLoginLogo.svg";
import GoogleLogo from "~/assets/svg/GoogleLogo.svg";

// in the loader of the login route
export async function loader({ request }: LoaderFunctionArgs) {
  try {
    // Get the current session if it exists
    const session = await getSession(request.headers.get("cookie"));
    
    // Create a new empty session
    const newSession = await getSession();

    // Read any error left by a failed auth attempt. Consume it here and do
    // NOT re-flash it into the new session: re-injecting it made the error
    // sticky, so the "Username or password is incorrect" text rendered on
    // every fresh page load before the user typed anything.
    const error = session.get(authenticator.sessionErrorKey);

    // Destroy the old session and get its cookie
    const oldSessionCookie = await destroySession(session);
    
    // Return the new session with both cookies to ensure old one is cleared
    return json(
      { error, googleSsoEnabled },
      {
        headers: {
          "Set-Cookie": [
            oldSessionCookie,
            await commitSession(newSession)
          ].join(", "),
        },
      }
    );
  } catch (error) {
    // If there's an error, just create a fresh session
    const newSession = await getSession();
    return json(
      { error: null, googleSsoEnabled },
      {
        headers: {
          "Set-Cookie": await commitSession(newSession),
        },
      }
    );
  }
}

export async function action({
  request,
}: ActionFunctionArgs) {
  try {
    const response = await authenticator.authenticate("user-pass", request, {
      successRedirect: "/",
      failureRedirect: "/auth/login",
    });
    
    // The response will be a redirect response
    return response;
  } catch (error) {
    // If this is a redirect response, return it
    if (error instanceof Response && error.status === 302) {
      return error;
    }
    return json(
      { error: error instanceof Error ? error.message : "Authentication failed" },
      { status: 401 }
    );
  }
}

export default function Auth() {
  const actionData = useActionData<typeof action>();
  const { error, googleSsoEnabled } = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const isLoading = navigation.state === "submitting";

  // Google-flow errors arrive via ?error=<code> query string from the
  // failureRedirect in /auth/google/callback. The loader's `error` channel
  // only carries password-strategy errors, so we read the param separately.
  const [searchParams] = useSearchParams();
  const googleErrorCode = searchParams.get("error");
  const googleErrorMessage =
    googleErrorCode === "google"
      ? "Google sign-in failed. Try again or use your username and password below."
      : null;

  return (
    <Flex align='center' justify='center' w={'100%'} h={'100vh'}>
      <Box w={'100%'} pos={'absolute'} top={0} h={'100%'} style={{zIndex:-1}} opacity={.9} bg={'black'}/>
      <Flex direction={'column'} justify="center" align="center" pos={'absolute'}>
        {isLoading && (
          <Flex justify="center" mb="md">
            <Loader color="white" size="md" />
          </Flex>
        )}
        <Flex direction={'column'} justify="space-between" align="center" gap={'1.5rem'} style={{ borderRadius: '10px', shadow: 'md' }} bg={'#0A121F'} p={32}>
          <Flex align="center" direction={'column'} gap={'1.5rem'} style={{ zIndex: 1}}>
            <Flex direction={'column'} gap={16} align={'left'} w={'100%'}>
              <Flex align={'center'} justify={'center'}>
                <Image alt="Login" src={TrashFairiesLogo} w={'50%'} />
              </Flex>
              <Text fz="30px" fw="600" style={{ textAlign: 'left', lineHeight: 1.2 }} c={"textColor.0"}>
                Sign In
              </Text>
              <Text fz="16px" fw="400" mt=".5rem" c={"dimmed"}>
                Enter your credentials to view your apps
              </Text>
            </Flex>
          </Flex>

          {googleSsoEnabled && (
            <Flex direction="column" gap="md" w="100%">
              <Button
                component="a"
                href="/auth/google"
                variant="white"
                color="dark"
                size="md"
                fullWidth
                disabled={isLoading}
                leftSection={<Image alt="" src={GoogleLogo} w={20} h={20} />}
                styles={{ label: { fontWeight: 600 } }}
              >
                Sign in with Google
              </Button>
              {googleErrorMessage && (
                <Alert color="red" title="Google sign-in failed">
                  {googleErrorMessage}
                </Alert>
              )}
              <Divider label="or" labelPosition="center" c="dimmed" />
            </Flex>
          )}

          <Form method="POST" style={{ width: '100%' }}>
            <Flex direction="column" gap="md" w={'100%'}>
              <TextInput 
                name="username" 
                label="Username / Email" 
                placeholder="Email" 
                required 
                c={"textColor.0"} 
                w={'100%'} 
                variant="filled"
                disabled={isLoading}
              />
              <PasswordInput 
                name="password" 
                label="Password" 
                placeholder="Password" 
                type="password" 
                required 
                c={"textColor.0"}
                disabled={isLoading}
              />
              {actionData?.error && (
                <Alert color="red" title="Error">
                  {actionData.error}
                </Alert>
              )}
              <Flex justify={'space-between'} align={'center'}>
                <Checkbox label='remember me' c={'dimmed'} disabled={isLoading}/>
                <Button type="submit" color="white" variant="outline" loading={isLoading}>
                  Sign in
                </Button>
              </Flex>
              {(actionData?.error || error) && (
                <Text c="red" size="sm" ta="center" mt="xs">
                  Username or password is incorrect
                </Text>
              )}
            </Flex>
          </Form>
        </Flex>
      </Flex>
    </Flex>
  );
}
